import type { PublicAccount } from '@postpilot/core/domain';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  CalendarClock,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  PenSquare,
  Plus,
  Settings,
  Users,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/primitives';
import { AccountRowSkeleton } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { useAccountsStore } from '@/stores/accounts-store';
import { useUiStore } from '@/stores/ui-store';

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/compose', label: 'Compose', icon: PenSquare, end: false },
  { to: '/queue', label: 'Queue', icon: CalendarClock, end: false },
  { to: '/accounts', label: 'Accounts', icon: Users, end: false },
  { to: '/settings', label: 'Settings', icon: Settings, end: false },
] as const;

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  const accounts = useAccountsStore((s) => s.accounts);
  const loading = useAccountsStore((s) => s.loading);
  const connect = useAccountsStore((s) => s.connect);

  return (
    <motion.aside
      // Animating width (not transform) is unusual — it forces layout. It is the
      // right call here because the main content must actually reflow into the
      // reclaimed space; a transform would slide the sidebar over the content.
      // One element, 200ms, on an explicit user action: the cost is invisible.
      animate={{ width: collapsed ? 64 : 256 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'border-sidebar-border bg-sidebar relative z-20 flex h-dvh shrink-0 flex-col border-r',
        // Off-canvas on mobile — a 256px sidebar on a 375px screen leaves no room
        // for the actual product. The topbar gets a menu button instead.
        'hidden md:flex',
      )}
    >
      {/* Brand */}
      <div className="border-sidebar-border flex h-14 items-center gap-2.5 border-b px-4">
        <div className="bg-primary text-primary-foreground flex size-7 shrink-0 items-center justify-center rounded-md">
          <svg viewBox="0 0 24 24" className="size-4" fill="none" aria-hidden>
            <path
              d="M4 19L20 5M20 5L12 19L10 13L4 11L20 5Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="truncate text-sm font-semibold tracking-tight"
            >
              PostPilot
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-0.5 p-2">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Accounts */}
      <div className="border-sidebar-border mt-2 flex min-h-0 flex-1 flex-col border-t pt-3">
        {!collapsed && (
          <div className="flex items-center justify-between px-4 pb-2">
            <span className="text-muted-foreground text-xs font-medium">Accounts</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void connect('/accounts')}
                  aria-label="Connect an X account"
                >
                  <Plus />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Connect an X account</TooltipContent>
            </Tooltip>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {loading ? (
            <>
              <AccountRowSkeleton />
              <AccountRowSkeleton />
            </>
          ) : accounts.length === 0 ? (
            !collapsed && (
              <button
                onClick={() => void connect('/accounts')}
                className={cn(
                  'border-sidebar-border w-full rounded-lg border border-dashed p-3 text-left transition-colors',
                  'hover:border-primary/40 hover:bg-sidebar-accent',
                )}
              >
                <span className="block text-xs font-medium">No accounts yet</span>
                <span className="text-muted-foreground mt-0.5 block text-xs">
                  Connect X to start posting
                </span>
              </button>
            )
          ) : (
            accounts.map((account) => (
              <AccountRow key={account.id} account={account} collapsed={collapsed} />
            ))
          )}
        </div>
      </div>

      {/* Collapse */}
      <div className="border-sidebar-border border-t p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className={cn(
            'text-muted-foreground w-full justify-start gap-2.5',
            collapsed && 'justify-center px-0',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </Button>
      </div>
    </motion.aside>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  end,
  collapsed,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end: boolean;
  collapsed: boolean;
}) {
  const location = useLocation();
  const isActive = end ? location.pathname === to : location.pathname.startsWith(to);

  const link = (
    <NavLink
      to={to}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        collapsed && 'justify-center px-0',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
      )}
    >
      {/* The active indicator is a shared layout element, so it *slides* between
          nav items rather than blinking out and in. This is the single detail
          that most makes a sidebar feel like Linear's. */}
      {isActive && (
        <motion.span
          layoutId="nav-active-indicator"
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="bg-primary absolute inset-y-1 left-0 w-0.5 rounded-r-full"
        />
      )}
      <Icon className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  // Collapsed, the label is gone — so it has to come back as a tooltip, or the
  // icons are a guessing game.
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

function AccountRow({ account, collapsed }: { account: PublicAccount; collapsed: boolean }) {
  const isBroken = account.status !== 'active';

  const row = (
    <div
      className={cn(
        'hover:bg-sidebar-accent flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors',
        collapsed && 'justify-center px-0',
      )}
    >
      <div className="relative shrink-0">
        <Avatar className="size-7">
          <AvatarImage src={account.avatarUrl ?? undefined} alt="" />
          <AvatarFallback>{account.username.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>

        {/* A status dot on the avatar. A broken account is the one thing a user
            must notice without being told — a scheduled post to it will fail. */}
        <span
          className={cn(
            'border-sidebar absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2',
            isBroken ? 'bg-status-failed' : 'bg-status-published',
          )}
          aria-hidden
        />
      </div>

      {!collapsed && (
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">@{account.username}</div>
          <div
            className={cn(
              'truncate text-[11px]',
              isBroken ? 'text-status-failed' : 'text-muted-foreground',
            )}
          >
            {isBroken ? 'Needs reconnecting' : (account.displayName ?? 'Connected')}
          </div>
        </div>
      )}

      {!collapsed && isBroken && (
        <AlertCircle className="text-status-failed size-3.5 shrink-0" aria-hidden />
      )}
    </div>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right">
          @{account.username}
          {isBroken && ' — needs reconnecting'}
        </TooltipContent>
      </Tooltip>
    );
  }

  return row;
}
